// ========= START: src/hooks/useSpeechRecognition.js (Push-to-Talk) =========

import { useState, useEffect, useRef } from 'react';
import { appendDeduped } from '../utils/transcript';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;

// Phones/tablets force-stop the speech engine after a pause and IGNORE
// `continuous = true`. There is no setting that overrides that — it's baked into
// the mobile browsers. So on mobile we stop fighting it: each tap captures ONE
// clean utterance and submits when the student stops talking (exactly how Google
// Translate's mic behaves). That removes the restart loop that dropped letters
// and caused the "my my name" stutter. Desktop keeps the continuous Speak/Done
// flow below, because desktop Chrome genuinely honors `continuous` and works.
const IS_MOBILE =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS reports as desktop Mac

if (recognition) {
  recognition.continuous = !IS_MOBILE; // desktop: stay open across pauses; mobile: one clean phrase
  recognition.lang = 'en-US';
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
}

export const useSpeechRecognition = () => {
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const recognitionRef = useRef(recognition);
  // Speech captured during the CURRENT speaking turn. We keep these in refs (not
  // state) so they survive the rapid re-renders and the mobile auto-restarts
  // described below.
  //   committedRef     — text finalized in EARLIER recognition sessions this turn
  //                      (each mobile auto-restart starts a brand-new session).
  //   sessionFinalRef  — text finalized in the CURRENT session, rebuilt fresh on
  //                      every onresult so re-fired results never double up.
  //   interimRef       — the latest interim (not-yet-final) guess.
  const committedRef = useRef('');
  const sessionFinalRef = useRef('');
  const interimRef = useRef('');

  useEffect(() => {
    const rec = recognitionRef.current;
    if (!rec) {
      console.error("Speech Recognition is not supported in this browser.");
      return;
    }

    rec.onstart = () => {
      setIsListening(true);
    };

    rec.onend = () => {
      if (IS_MOBILE) {
        // MOBILE: the engine stops on its own when the student stops talking
        // (or when they tap Stop). Do NOT restart — restarting is exactly what
        // dropped letters (the missing H, N) and caused the stutter. Submit the
        // one clean phrase we captured. The engine's own end-of-speech detection
        // is what ended it (same as Google Translate), so it won't cut in too
        // early like the old silence-timer version did.
        const sessionText = appendDeduped(sessionFinalRef.current, interimRef.current);
        const captured = appendDeduped(committedRef.current, sessionText);
        setIsListening(false);
        if (captured) {
          setFinalTranscript(captured);
        }
        return;
      }

      // DESKTOP (unchanged): desktop Chrome honors `continuous = true`, so keep
      // one continuous turn alive by restarting until the student taps Done
      // (which sets stoppedByApp). Pauses to think don't cut the student off.
      if (!rec.stoppedByApp) {
        // The session that just ended is about to be discarded (its
        // event.results resets on restart). Fold whatever it captured —
        // including the trailing interim word the student was mid-saying —
        // into the committed text BEFORE restarting, so a word isn't lost in
        // the restart gap. appendDeduped drops the overlap when the next session
        // re-recognises the same tail audio.
        const sessionText = appendDeduped(sessionFinalRef.current, interimRef.current);
        if (sessionText) {
          committedRef.current = appendDeduped(committedRef.current, sessionText);
        }
        sessionFinalRef.current = '';
        interimRef.current = '';
        try {
          rec.start();
        } catch (e) {
          // Engine hasn't fully released yet; the next onend will retry.
        }
        return;
      }
      setIsListening(false);
    };

    rec.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      // 'no-speech' / 'audio-capture' are transient (e.g. the student paused).
      // On desktop, leaving stoppedByApp false lets onend restart so they keep
      // their turn; on mobile onend just ends the turn. Other errors stop us.
      if (event.error !== 'no-speech' && event.error !== 'audio-capture') {
        rec.stoppedByApp = true;
        setIsListening(false);
      }
    };

    rec.onresult = (event) => {
      // REBUILD the current session's transcript from scratch every time rather
      // than appending deltas. event.results is the cumulative list for THIS
      // session, so walking it from 0 and assigning (not +=) means a result
      // that the engine re-fires can never be counted twice — that double-
      // counting is what produced "My my my name my name is" on mobile.
      let interimTranscript = '';
      let sessionFinalTranscript = '';

      for (let i = 0; i < event.results.length; i++) {
        const currentTranscript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          // Merge with overlap removal: the engine often re-fires a longer
          // version of the same segment ("my name is" then "my name is John"),
          // which plain concatenation turned into "my name is my name is John".
          sessionFinalTranscript = appendDeduped(sessionFinalTranscript, currentTranscript);
        } else {
          interimTranscript += currentTranscript;
        }
      }

      sessionFinalRef.current = sessionFinalTranscript.trim();
      interimRef.current = interimTranscript.trim();

      // Live feedback for the UI: text committed from earlier sessions +
      // this session's words + the current interim guess. appendDeduped removes
      // any overlap where a mobile restart re-heard the previous tail.
      const liveText = appendDeduped(
        appendDeduped(committedRef.current, sessionFinalRef.current),
        interimRef.current
      );
      setTranscript(liveText);
    };

    return () => {
      if (rec) {
        rec.stoppedByApp = true;
        rec.onstart = null;
        rec.onend = null;
        rec.onerror = null;
        rec.onresult = null;
        try {
          rec.stop();
        } catch (e) {
          // Ignore error if recognition wasn't running
        }
      }
    };
  }, []);

  // PUSH-TO-TALK: the student tapped "Tap to Speak" — open a fresh turn.
  const startListening = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    rec.stoppedByApp = false;
    committedRef.current = '';
    sessionFinalRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setFinalTranscript('');
    setIsListening(true);
    try {
      rec.start();
    } catch (e) {
      // Already running (e.g. a stray double-tap) — keep the existing session.
      console.warn('Speech recognition already running, continuing with existing session');
    }
  };

  // The student tapped "Done"/"Stop" — end the engine. On mobile this is an
  // OPTIONAL early-cut: the engine usually ends on its own, but tapping stops it
  // sooner. On desktop it's the normal way to end the continuous turn.
  const stopListening = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    rec.stoppedByApp = true; // tell the desktop onend NOT to restart — the turn is over
    try {
      rec.stop();
    } catch (e) {
      console.warn('Error stopping speech recognition:', e);
    }
    setIsListening(false);
    if (!IS_MOBILE) {
      // Desktop commits here. On mobile the commit happens in onend (fired by
      // rec.stop() above, and also when the engine ends on its own), so
      // committing here too would be redundant.
      const sessionText = appendDeduped(sessionFinalRef.current, interimRef.current);
      const captured = appendDeduped(committedRef.current, sessionText);
      if (captured) {
        setFinalTranscript(captured);
      }
    }
  };

  // Lets App.js clear the transcript after processing it.
  const resetTranscript = () => {
    committedRef.current = '';
    sessionFinalRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setFinalTranscript('');
  };

  return {
    transcript,
    finalTranscript,
    isListening,
    startListening,
    stopListening,
    hasRecognitionSupport: !!recognition,
    resetTranscript,
  };
};

// ========= END: src/hooks/useSpeechRecognition.js (Push-to-Talk) =========
